import React, { useState, useEffect } from 'react';
import { getHighScores, saveHighScore, isHighScore } from '../utils/highscores';
import './ResultScreen.css';

const OP_LABELS = {
  add: 'Plus',
  subtract: 'Minus',
  multiply: 'Mal',
  divide: 'Geteilt',
  expand: 'Erweitern',
  reduce: 'Kürzen',
};

const DIFF_LABELS = {
  easy: 'Einfach',
  medium: 'Mittel',
  hard: 'Schwer',
};

export default function ResultScreen({ result, settings, onBackToMenu }) {
  const [highScores, setHighScores] = useState([]);
  const [playerName, setPlayerName] = useState('');
  const [nameSaved, setNameSaved] = useState(false);
  const [madeHighScore, setMadeHighScore] = useState(false);
  const [showContent, setShowContent] = useState(false);

  useEffect(() => {
    const scores = getHighScores();
    setHighScores(scores);
    setMadeHighScore(isHighScore(result.score));
    setTimeout(() => setShowContent(true), 300);
  }, [result.score]);

  const handleSaveName = () => {
    if (!playerName.trim()) return;
    const updated = saveHighScore({
      name: playerName.trim(),
      score: result.score,
      difficulty: settings.difficulty,
      operations: settings.operations,
      numberType: settings.numberType,
      duration: settings.duration,
    });
    setHighScores(updated);
    setNameSaved(true);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') handleSaveName();
  };

  const accuracy = result.questionsAnswered > 0
    ? Math.round((result.correctCount / result.questionsAnswered) * 100)
    : 0;

  return (
    <div className="result-container">
      <div className={`result-content ${showContent ? 'visible' : ''}`}>
        {/* Score reveal */}
        <div className="score-reveal">
          <h2 className="result-title">Runde beendet!</h2>
          <div className="final-score">
            <span className="final-score-label">Endpunktzahl</span>
            <span className="final-score-value">{result.score}</span>
          </div>
        </div>

        {/* Stats */}
        <div className="result-stats">
          <div className="result-stat">
            <span className="result-stat-value">{result.questionsAnswered}</span>
            <span className="result-stat-label">Aufgaben</span>
          </div>
          <div className="result-stat">
            <span className="result-stat-value">{result.correctCount}</span>
            <span className="result-stat-label">Richtig</span>
          </div>
          <div className="result-stat">
            <span className="result-stat-value">{accuracy}%</span>
            <span className="result-stat-label">Genauigkeit</span>
          </div>
        </div>

        {/* Settings summary */}
        <div className="result-settings">
          <span>{DIFF_LABELS[settings.difficulty]}</span>
          <span className="dot">·</span>
          <span>{settings.operations.map(op => OP_LABELS[op]).join(', ')}</span>
          <span className="dot">·</span>
          <span>{settings.numberType === 'fractions' ? 'Brüche' : 'Natürliche Zahlen'}</span>
          <span className="dot">·</span>
          <span>{settings.duration} Min</span>
        </div>

        {/* High score entry */}
        {madeHighScore && !nameSaved && (
          <div className="highscore-entry">
            <div className="hs-badge">Neuer Highscore!</div>
            <p className="hs-prompt">Trage deinen Namen ein:</p>
            <div className="hs-input-row">
              <input
                type="text"
                className="hs-input"
                value={playerName}
                onChange={(e) => setPlayerName(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Dein Name"
                maxLength={20}
                autoFocus
              />
              <button className="hs-save-btn" onClick={handleSaveName}>
                Speichern
              </button>
            </div>
          </div>
        )}

        {nameSaved && (
          <div className="hs-saved-msg">
            Gespeichert!
          </div>
        )}

        {/* High score table */}
        <div className="highscore-table">
          <h3 className="hs-title">Bestenliste</h3>
          {highScores.length === 0 ? (
            <p className="hs-empty">Noch keine Einträge.</p>
          ) : (
            <div className="hs-list">
              {highScores.map((entry, idx) => (
                <div
                  key={idx}
                  className={`hs-row ${
                    nameSaved && entry.name === playerName.trim() && entry.score === result.score
                      ? 'hs-highlight'
                      : ''
                  }`}
                  style={{ animationDelay: `${idx * 0.05}s` }}
                >
                  <span className="hs-rank">
                    {idx === 0 ? '🥇' : idx === 1 ? '🥈' : idx === 2 ? '🥉' : `${idx + 1}.`}
                  </span>
                  <span className="hs-name">{entry.name}</span>
                  <span className="hs-score">{entry.score}</span>
                  <span className="hs-meta">
                    {DIFF_LABELS[entry.difficulty]} · {(entry.operations || [entry.operation]).map(op => OP_LABELS[op]).filter(Boolean).join(', ')}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>

        <button className="back-btn" onClick={onBackToMenu}>
          <span>Zurück zum Menü</span>
          <span className="back-arrow">←</span>
        </button>
      </div>
    </div>
  );
}
